import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { SKIP_GLOBAL_ERROR_HANDLING } from '../../../../../../core/constants/http-context-tokens';
import { AccountTypeObject, IndustryObject } from '../../../../../../core/models/entities';

@Injectable({ providedIn: 'root' })
export class GeneralOrganizationService {
  private url = `${environment.apiUrl}/v1/organization/administrative/ckb`;
  private accountTypeUrl = `${environment.apiUrl}/v1/organization/autocomplete/accounttype`;
  private industryUrl = `${environment.apiUrl}/v1/organization/autocomplete/industry`;
  private http = inject(HttpClient);
  constructor() {}

  public getIndustries() {
    return this.http.get<IndustryObject>(this.industryUrl).pipe(map((response) => response));
  }
  public getAccountTypes() {
    return this.http.get<AccountTypeObject>(this.accountTypeUrl).pipe(map((response) => response));
  }
  /** The caller shows the errors: a 400 on a Stellar/S1 id goes under that field. */
  public updateCkbApi(generalRequest: GeneralOrganizationServiceCkbRequest) {
    const url = `${this.url}(${generalRequest.organization_id})/`;
    return this.http.put<GeneralOrganizationServiceCkbRequest>(url, generalRequest, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLING, true),
    });
  }

  public getCkbById(orgId: number) {
    const url = `${this.url}(${orgId})/`;
    return this.http.get<GeneralOrganizationServiceCkbRequest>(url).pipe(map((response) => response));
  }

  public assignUserToOrganization(orgId: number, user: number) {
    const url = `${environment.apiUrl}/v1/organization/administrative/organization(${orgId})/add_user_to_organization/?user=${user}`;
    return this.http.post(url, { user });
  }

  public removeUserFromOrganization(orgId: number, user: number) {
    const url = `${environment.apiUrl}/v1/organization/administrative/organization(${orgId})/remove_user_from_organization/?user=${user}`;
    return this.http.delete(url);
  }

  public listOrganizationUsers(orgId: number) {
    const url = `${environment.apiUrl}/v1/organization/administrative/organization(${orgId})/list_users_in_organization/`;
    return this.http.get<UsersResponse>(url).pipe(map((response) => response.value));
  }

  public listAvailableOrganizationUsers(orgId: number) {
    // TODO - do virtual scrolling and server side search instead of hardcode top to 1000
    const url = `${environment.apiUrl}/v1/organization/administrative/organization(${orgId})/list_available_users/?$top=1000`;
    return this.http.get<UsersResponse>(url).pipe(map((response) => response.value));
  }
}

export interface UsersResponse {
  value: [
    {
      id: number;
      avatar: string;
      is_admin: boolean;
      groups: string[];
      last_login: Date;
      username: string;
      first_name: string;
      last_name: string;
      email: string;
      is_active: boolean;
      date_joined: Date;
    },
  ];
}

export interface GeneralOrganizationServiceCkbRequest {
  organization_id: number;
  mdr_windows: string;
  mdr_linux: string;
  mdr_mac: string;
  xdr_windows_agents: string;
  xdr_linux_agents: string;
  xdr_syslog_senders: string;
  xdr_api_connectors: string;
  xdr_network_sensors: string;
  stellar_cyber_id: string;
  stellar_cyber_name: string;
  sentinel_one_id: string;
  sentinel_one_name: string;
  cyrisma_id: string;
  cyrisma_name: string;
  assure_id: string;
  assure_name: string;
  chronicle_id: string;
  chronicle_name: string;
  xrai_id: string;
  xrai_name: string;
  polymer_id: string;
  polymer_name: string;
  zoho_name: string;
  zoho_id: string;
  industry: string;
  checkpoint_id: string;
  checkpoint_name: string;
  crowdstrike_edr_id: string;
  crowdstrike_edr_name: string;
  crowdstrike_xdr_id: string;
  crowdstrike_xdr_name: string;
  vicarius_id: string;
  vicarius_name: string;
  zenskar_id: string;
  zenskar_name: string;
}
